import { useEffect } from 'react'

interface UseLeftPanelVisibilitySyncInput {
  isMobile: boolean
  isExpanded: boolean
  mobileOpen: boolean
  setIsExpanded: (expanded: boolean) => void
  setMobileOpen: (open: boolean) => void
  setActiveItem: (item: string) => void
}

/**
 * Keeps the panel's open state in sync with the viewport and the right panel: auto-closes on a
 * switch to mobile, closes the two panels against each other on mobile (via the
 * `left-panel-opened` / `right-panel-opened` window events), and falls back to Select when closed.
 */
export function useLeftPanelVisibilitySync({
  isMobile,
  isExpanded,
  mobileOpen,
  setIsExpanded,
  setMobileOpen,
  setActiveItem,
}: UseLeftPanelVisibilitySyncInput) {
  // When entering lower-width devices, auto-close any expanded panel
  useEffect(() => {
    if (isMobile) {
      setIsExpanded(false)
      setMobileOpen(false)
    }
  }, [isMobile, setIsExpanded, setMobileOpen])

  // Notify others when left panel opens on mobile
  useEffect(() => {
    if (isMobile && mobileOpen) {
      window.dispatchEvent(new Event('left-panel-opened'))
    }
  }, [isMobile, mobileOpen])

  // Close if right panel opens on mobile
  useEffect(() => {
    const closeHandler = () => {
      if (isMobile) {
        setMobileOpen(false)
        setIsExpanded(false)
      }
    }

    window.addEventListener('right-panel-opened', closeHandler)
    return () => window.removeEventListener('right-panel-opened', closeHandler)
  }, [isMobile, setIsExpanded, setMobileOpen])

  useEffect(() => {
    if (!isExpanded && !mobileOpen) {
      setActiveItem('select')
    }
  }, [isExpanded, mobileOpen, setActiveItem])
}
